import React, { Ref, RefObject } from "react";
import { useSpring, animated } from "@react-spring/web";
import { useDrag } from "@use-gesture/react";
import { classNames, doNothing } from "@greeter/util";
import { throttle } from "lodash";

import css from "./Swipable.module.scss";

export type PositionHandler = (
  down: boolean,
  x: number,
  y: number,
  velocity: number,
  distance: number
) => { x: number; y: number };

export type SwipableProps = React.PropsWithChildren & {
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  positionHandler?: PositionHandler;
  onRelease?: () => void;
};

export function Swipable({
  className,
  children,
  positionHandler = (down, x) => ({ x: x > 0 && down ? x : 0, y: 0 }),
  onRelease = doNothing,
  ...props
}: SwipableProps & React.ComponentProps<"div">) {
  const [{ xy }, setPosition] = useSpring(() => ({ xy: [0, 0] }));

  const bind = useDrag(
    ({ down, movement: [mx, my], distance: [dx, dy], velocity: [vx, vy] }) => {
      const newPos = positionHandler(down, mx, my, vx, dx);
      setPosition({ xy: [newPos.x, newPos.y] });
      if (!down) throttle(onRelease, 200);
    }
  );

  return (
    <animated.div
      ref={props.ref}
      {...classNames(css.Swipable, className)}
      {...bind()}
      style={{
        transform: xy.to((x, y) => `translate3d(${x}px, ${y}px, 0)`),
      }}
    >
      {children}
    </animated.div>
  );
}
